import { Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Navbar, Sidebar } from "@/components/layout";
import { ErrorBoundary, RouteError } from "@/components/error";
import { RouteLoading } from "@/components/common";
import { useAuthStore } from "@/features/auth";
import { useIsMobile } from "@/hooks/useIsMobile";
import { PATHS } from "@/lib/router";
import { ROLES } from "@/lib/constants/options";
import { cn } from "@/lib/utils";

const AppLayout = observer(() => {
  const location = useLocation();
  const { user } = useAuthStore();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();

  const isCompanySetupPath = location.pathname === PATHS.COMPANY_SETUP;
  const isFixedHeightPath = location.pathname === "/data-entry";
  const isAdminRole = [ROLES.SYSTEM_ADMIN, ROLES.BUSINESS_UNIT].includes(
    user?.role,
  );
  const needsCompanySetup = Boolean(user && !isAdminRole && !user?.companyId);
  const hideChrome = isCompanySetupPath && needsCompanySetup;
  const shouldShowSidebar = !user?.delegationOnlyAccess;

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile]);

  if (hideChrome) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-background">
        <Navbar
          sidebarCollapsed={sidebarCollapsed}
          showBrand
          showMobileMenuButton={false}
          onMobileMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />
        <main className="min-h-[calc(100vh-3.5rem)] min-h-[calc(100dvh-3.5rem)] bg-background px-3 py-4 sm:px-4">
          <div className="container mx-auto w-full max-w-6xl">
            <ErrorBoundary fallback={RouteError}>
              <Suspense fallback={<RouteLoading />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
    );
  }

  const mainOverflowClass = isFixedHeightPath
    ? "overflow-hidden"
    : "overflow-y-auto";
  const containerOverflowClass = isFixedHeightPath
    ? "overflow-hidden"
    : "overflow-y-auto";
  const containerHeightClass = isFixedHeightPath
    ? "min-h-0 h-full"
    : "min-h-full";

  return (
    <div className="flex min-h-screen min-h-[100dvh] flex-col overflow-hidden bg-background lg:h-screen lg:flex-row">
      {/* Desktop Sidebar */}
      {shouldShowSidebar && (
        <div className="hidden h-full lg:block">
          <Sidebar
            collapsed={sidebarCollapsed}
            onCollapseChange={setSidebarCollapsed}
          />
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && shouldShowSidebar && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <div className="relative h-full w-[min(18rem,calc(100vw-2rem))] shadow-lg">
            <Sidebar
              collapsed={false}
              mobile
              onCollapseChange={() => setMobileSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Navbar */}
        <Navbar
          sidebarCollapsed={sidebarCollapsed}
          showBrand={!shouldShowSidebar || isMobile}
          showMobileMenuButton={shouldShowSidebar}
          onMobileMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />

        {/* Page Content */}
        <main
          className={cn(
            "flex-1 min-h-0 bg-background px-3 py-3 sm:px-4 sm:py-4",
            isFixedHeightPath && "h-full",
            mainOverflowClass,
          )}
        >
          <div
            className={cn(
              "mx-auto flex w-full min-w-0 flex-col bg-card/70 px-3 py-3 sm:px-4 sm:py-4",
              "rounded-none sm:rounded-radius 2xl:container",
              containerHeightClass,
              containerOverflowClass,
            )}
          >
            <ErrorBoundary fallback={RouteError}>
              <Suspense fallback={<RouteLoading />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
});

export default AppLayout;
